import { Link } from "@tanstack/react-router";
import { Card, CardBody, MetaPill } from "@flint/ui";
import { StoreAdminLayout } from "./store-admin-layout.tsx";

export interface StoreModulePlaceholderProps {
  /** Module name — the page title and the breadcrumb leaf. */
  title: string;
  /** Milestone that builds this module, e.g. `"M3"`. */
  milestone: string;
  /** One sentence on what the module will do. */
  summary: string;
}

/**
 * "Coming soon" page for a Store admin module that isn't built yet.
 *
 * Every Store nav link routes somewhere real so the sidebar can be walked
 * end-to-end today. This is a deliberate departure from `nav-config.ts`'s
 * `disabled` pattern for unbuilt surfaces — a named coming-soon page beats a
 * dead link while most of the module list is still ahead of us.
 *
 * Delete a module's usage as soon as its real page lands.
 *
 * @see docs/ecom/v1.0.1-tasks.md — the milestone each module belongs to
 */
export function StoreModulePlaceholder({
  title,
  milestone,
  summary,
}: StoreModulePlaceholderProps) {
  return (
    <StoreAdminLayout breadcrumbs={[{ label: "Store", to: "/store/admin" }, { label: title }]}>
      <Card>
        <CardBody>
          <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
            <MetaPill tone="warn">Coming soon · {milestone}</MetaPill>
            <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">{title}</h1>
            <p className="mt-1.5 max-w-md text-sm text-muted">{summary}</p>
            <Link
              to="/store/admin"
              className="mt-2 text-sm font-medium text-sky-700 hover:underline"
            >
              ← Back to Store dashboard
            </Link>
          </div>
        </CardBody>
      </Card>
    </StoreAdminLayout>
  );
}
